import React, { useEffect, useMemo, useState, useCallback } from 'react';
import apinode from '../../services/apinode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruckFast, faChevronLeft, faChevronRight, faCalendarDay, faChevronDown,
  faTriangleExclamation, faCircleCheck, faClock, faBoxOpen, faXmark,
  faBoxesStacked, faLocationDot, faCodeBranch, faStore, faQuestion,
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../../components/navbar/navbar';

const DIAS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const DIAS_CURTO = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'];
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function segundaDaSemana(ref) {
  const d = new Date(ref);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
}
const addDias = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtCurto = (d) => `${pad(d.getDate())} ${MESES[d.getMonth()]}`;
function fmtDataHora(v) { if (!v) return '—'; const d = new Date(v); if (isNaN(d)) return '—'; return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; }
function fmtData(v) { if (!v) return '—'; const d = new Date(v.length === 10 ? v + 'T00:00:00' : v); if (isNaN(d)) return '—'; return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`; }
const brl = (v) => (v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

export default function PainelDespachos() {
  const [refDate, setRefDate] = useState(() => segundaDaSemana(new Date()));
  const [apelido, setApelido] = useState('');
  const [apelidos, setApelidos] = useState([]);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [diaAberto, setDiaAberto] = useState(null);

  const segunda = useMemo(() => segundaDaSemana(refDate), [refDate]);
  const domingo = useMemo(() => addDias(segunda, 6), [segunda]);
  const hojeIso = iso(new Date());
  const conta = localStorage.idconta;

  // apelidos da semana
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apinode.get(`/despachos/apelidos/${conta}`, {
          params: { ini: iso(segunda), fim: iso(domingo) },
        });
        setApelidos(data || []);
      } catch { /* silencioso */ }
    })();
  }, [conta, segunda, domingo]);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const { data } = await apinode.get(`/despachos/painel/${conta}`, {
        params: { ini: iso(segunda), fim: iso(domingo), apelido: apelido || undefined },
      });
      setDados(data);
    } catch (e) {
      setErro(e.response?.data?.message || e.message);
    } finally { setLoading(false); }
  }, [conta, segunda, domingo, apelido]);

  useEffect(() => { carregar(); }, [carregar]);

  const porData = useMemo(() => {
    const m = {}; (dados?.dias || []).forEach((d) => { m[d.data] = d; }); return m;
  }, [dados]);

  const semana = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = addDias(segunda, i); const key = iso(d);
    return {
      idx: i, date: d, key, isHoje: key === hojeIso,
      ag: porData[key] || { total: 0, despachados: 0, atrasados: 0, noPrazo: 0, divergentes: 0, status: {} },
    };
  }), [segunda, porData, hojeIso]);

  const resumo = dados?.resumo || { total: 0, despachados: 0, atrasados: 0, noPrazo: 0, divergentes: 0 };
  const maxDia = useMemo(() => Math.max(1, ...semana.map((s) => s.ag.total)), [semana]);
  const rotuloSemana = `${fmtCurto(segunda)} – ${fmtCurto(domingo)} ${domingo.getFullYear()}`;

  return (
    <>
      <Navbar />
      <style>{CSS}</style>

      <div className="exp-wrap">
        <header className="exp-head">
          <div>
            <div className="exp-eyebrow">Expedição</div>
            <h1 className="exp-title"><FontAwesomeIcon icon={faTruckFast} /> Painel de despachos</h1>
          </div>
          <div className="exp-controls">
            <div className="exp-store">
              <FontAwesomeIcon icon={faStore} />
              <select value={apelido} onChange={(e) => setApelido(e.target.value)}>
                <option value="">Todas as contas</option>
                {apelidos.map((a) => (
                  <option key={a.apelido} value={a.apelido}>{a.apelido}</option>
                ))}
              </select>
            </div>
            <div className="exp-nav">
              <button className="exp-navbtn" onClick={() => setRefDate(addDias(segunda, -7))} aria-label="Semana anterior"><FontAwesomeIcon icon={faChevronLeft} /></button>
              <div className="exp-week"><FontAwesomeIcon icon={faCalendarDay} /><span>{rotuloSemana}</span></div>
              <button className="exp-navbtn" onClick={() => setRefDate(addDias(segunda, 7))} aria-label="Próxima semana"><FontAwesomeIcon icon={faChevronRight} /></button>
              <button className="exp-today" onClick={() => setRefDate(segundaDaSemana(new Date()))}>Hoje</button>
            </div>
          </div>
        </header>

        <section className="exp-stats" aria-label="Resumo da semana">
          <Stat n={resumo.total} label="Despachos" tone="ink" />
          <Stat n={resumo.divergentes} label="Divergências" tone="div" icon={faCodeBranch} />
          <Stat n={resumo.atrasados} label="Atrasados" tone="late" icon={faTriangleExclamation} />
          <Stat n={resumo.noPrazo} label="No prazo" tone="ontime" icon={faClock} />
          <Stat n={resumo.despachados} label="Despachados" tone="done" icon={faCircleCheck} />
        </section>

        {erro && <div className="exp-msg exp-msg--err">Não foi possível carregar: {erro}. Recarregue.</div>}

        {loading ? (
          <div className="exp-msg">Carregando a semana{apelido ? ` de ${apelido}` : ''}…</div>
        ) : (
          <section className="exp-grid">
            {semana.map((s) => (
              <DiaCard key={s.key} s={s} maxDia={maxDia} onClick={() => s.ag.total > 0 && setDiaAberto(s.key)} />
            ))}
          </section>
        )}

        <Legenda />
      </div>

      {diaAberto && <DetalheDia dataKey={diaAberto} conta={conta} apelido={apelido} onClose={() => setDiaAberto(null)} />}
    </>
  );
}

function DiaCard({ s, maxDia, onClick }) {
  const { ag } = s;
  const { total, atrasados, noPrazo, despachados, divergentes } = ag;
  const vazio = total === 0;
  const seg = (v) => (total ? (v / total) * 100 : 0);
  const alturaBarra = 6 + (total / maxDia) * 92;
  const statusChips = Object.entries(ag.status || {}).sort((a, b) => b[1] - a[1]).slice(0, 3);

  return (
    <button className={`dia ${s.isHoje ? 'dia--hoje' : ''} ${vazio ? 'dia--vazio' : ''} ${divergentes > 0 ? 'dia--div' : atrasados > 0 ? 'dia--alerta' : ''}`} onClick={onClick} disabled={vazio}>
      <div className="dia-top">
        <span className="dia-nome">{DIAS_CURTO[s.idx]}</span>
        <span className="dia-data">{pad(s.date.getDate())}/{pad(s.date.getMonth() + 1)}</span>
        {s.isHoje && <span className="dia-hoje-tag">hoje</span>}
      </div>

      <div className="dia-num">
        <span className="dia-num-val">{total}</span>
        <span className="dia-num-lbl">{total === 1 ? 'despacho' : 'despachos'}</span>
      </div>

      {divergentes > 0 && (
        <span className="dia-divtag"><FontAwesomeIcon icon={faCodeBranch} /> {divergentes} divergência{divergentes > 1 ? 's' : ''}</span>
      )}

      {vazio ? <div className="dia-empty">sem despachos</div> : (
        <>
          <div className="dia-load">
            <div className="dia-load-bar" style={{ height: `${alturaBarra}%` }}>
              {atrasados > 0 && <span className="seg seg-late" style={{ flex: seg(atrasados) }} />}
              {noPrazo > 0 && <span className="seg seg-ontime" style={{ flex: seg(noPrazo) }} />}
              {despachados > 0 && <span className="seg seg-done" style={{ flex: seg(despachados) }} />}
            </div>
          </div>
          <div className="dia-badges">
            {atrasados > 0 && <span className="bdg bdg-late"><FontAwesomeIcon icon={faTriangleExclamation} /> {atrasados} atrasado{atrasados > 1 ? 's' : ''}</span>}
            {noPrazo > 0 && <span className="bdg bdg-ontime"><FontAwesomeIcon icon={faClock} /> {noPrazo} no prazo</span>}
            {despachados > 0 && <span className="bdg bdg-done"><FontAwesomeIcon icon={faCircleCheck} /> {despachados} enviado{despachados > 1 ? 's' : ''}</span>}
          </div>
          {statusChips.length > 0 && (
            <div className="dia-status">
              {statusChips.map(([nome, q]) => <span className="stchip" key={nome} title={`${nome}: ${q}`}>{nome} <b>{q}</b></span>)}
            </div>
          )}
        </>
      )}
    </button>
  );
}

function Stat({ n, label, tone, icon }) {
  return (
    <div className={`stat stat--${tone}`}>
      <div className="stat-n">{icon && <FontAwesomeIcon icon={icon} />} {n}</div>
      <div className="stat-l">{label}</div>
    </div>
  );
}

function Legenda() {
  return (
    <div className="exp-legend">
      <span><i className="dot dot-div" /> Divergência — Atendido no ZipHub, confirmado NÃO despachado no ML</span>
      <span><i className="dot dot-late" /> Atrasado — prazo vencido, não saiu</span>
      <span><i className="dot dot-ontime" /> No prazo — dentro da data</span>
      <span><i className="dot dot-done" /> Despachado — já enviado</span>
    </div>
  );
}

function DetalheDia({ dataKey, conta, apelido, onClose }) {
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [expandido, setExpandido] = useState(null);
  const [filtro, setFiltro] = useState('todos'); // todos | atrasado | falta_atender | hoje | divergente

  useEffect(() => {
    let vivo = true;
    (async () => {
      setLoading(true); setErro(null);
      try {
        const { data } = await apinode.get(`/despachos/dia/${conta}`, { params: { data: dataKey, apelido: apelido || undefined } });
        if (vivo) setPedidos(data.pedidos || []);
      } catch (e) { if (vivo) setErro(e.response?.data?.message || e.message); }
      finally { if (vivo) setLoading(false); }
    })();
    return () => { vivo = false; };
  }, [conta, dataKey, apelido]);

  const d = new Date(dataKey + 'T00:00:00');
  const titulo = `${DIAS[(d.getDay() + 6) % 7]}, ${pad(d.getDate())} ${MESES[d.getMonth()]}`;
  const ehHoje = dataKey === iso(new Date());
  const divergentes = pedidos.filter((p) => p.divergencia === 'confirmada').length;

  // contadores dos filtros
  const cont = useMemo(() => ({
    todos: pedidos.length,
    atrasado: pedidos.filter((p) => p.situacaoDespacho === 'atrasado').length,
    falta_atender: pedidos.filter((p) => p.faltaAtender).length,
    hoje: pedidos.filter((p) => !p.despachado).length, // do dia, ainda não despachado
    divergente: pedidos.filter((p) => p.divergencia === 'confirmada').length,
  }), [pedidos]);

  const filtrados = useMemo(() => {
    switch (filtro) {
      case 'atrasado': return pedidos.filter((p) => p.situacaoDespacho === 'atrasado');
      case 'falta_atender': return pedidos.filter((p) => p.faltaAtender);
      case 'hoje': return pedidos.filter((p) => !p.despachado);
      case 'divergente': return pedidos.filter((p) => p.divergencia === 'confirmada');
      default: return pedidos;
    }
  }, [pedidos, filtro]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="exp-eyebrow">Despachos do dia{apelido ? ` · ${apelido}` : ''}</div>
            <h2 className="modal-title">{titulo}</h2>
            {divergentes > 0 && <span className="modal-div"><FontAwesomeIcon icon={faCodeBranch} /> {divergentes} divergência{divergentes > 1 ? 's' : ''} confirmada{divergentes > 1 ? 's' : ''}</span>}
          </div>
          <button className="modal-x" onClick={onClose} aria-label="Fechar"><FontAwesomeIcon icon={faXmark} /></button>
        </div>

        <div className="modal-body">
          {loading ? <div className="exp-msg">Carregando pedidos e conferindo o ML…</div>
            : erro ? <div className="exp-msg exp-msg--err">{erro}</div>
            : pedidos.length === 0 ? <div className="exp-msg"><FontAwesomeIcon icon={faBoxOpen} /> Nenhum despacho neste dia.</div>
            : (
              <div className="ped-list">
                <div className="fbar">
                  <FiltroBtn ativo={filtro === 'todos'} onClick={() => setFiltro('todos')} n={cont.todos} tone="ink">Todos</FiltroBtn>
                  <FiltroBtn ativo={filtro === 'divergente'} onClick={() => setFiltro('divergente')} n={cont.divergente} tone="div" icon={faCodeBranch}>Divergentes</FiltroBtn>
                  <FiltroBtn ativo={filtro === 'atrasado'} onClick={() => setFiltro('atrasado')} n={cont.atrasado} tone="late" icon={faTriangleExclamation}>Atrasados</FiltroBtn>
                  <FiltroBtn ativo={filtro === 'falta_atender'} onClick={() => setFiltro('falta_atender')} n={cont.falta_atender} tone="susp" icon={faBoxOpen}>Falta atender</FiltroBtn>
                  <FiltroBtn ativo={filtro === 'hoje'} onClick={() => setFiltro('hoje')} n={cont.hoje} tone="ontime" icon={faTruckFast}>
                    {ehHoje ? 'Despacha hoje' : 'A despachar'}
                  </FiltroBtn>
                </div>

                {filtrados.length === 0 ? (
                  <div className="exp-msg mini"><FontAwesomeIcon icon={faCircleCheck} /> Nenhum pedido neste filtro.</div>
                ) : (
                  <>
                    <div className="ped-hint">A divergência já vem conferida. Clique no pedido para ver itens e o envio completo.</div>
                    {filtrados.map((p) => (
                      <PedidoRow key={p.pedido} p={p} conta={conta} aberto={expandido === p.ship} onToggle={() => setExpandido(expandido === p.ship ? null : p.ship)} />
                    ))}
                  </>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function FiltroBtn({ ativo, onClick, n, tone, icon, children }) {
  return (
    <button className={`fbtn ${ativo ? 'fbtn--on' : ''} fbtn--${tone}`} onClick={onClick} disabled={n === 0 && !ativo}>
      {icon && <FontAwesomeIcon icon={icon} />} {children}
      <span className="fbtn-n">{n}</span>
    </button>
  );
}

function PedidoRow({ p, conta, aberto, onToggle }) {
  const [det, setDet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [verRaw, setVerRaw] = useState(false);

  useEffect(() => {
    if (!aberto || det || !p.ship) return;
    let vivo = true;
    (async () => {
      setLoading(true); setErro(null);
      try {
        const { data } = await apinode.get(`/despachos/shipment/${conta}`, { params: { ship: p.ship } });
        if (vivo) setDet(data);
      } catch (e) { if (vivo) setErro(e.response?.data?.message || e.message); }
      finally { if (vivo) setLoading(false); }
    })();
    return () => { vivo = false; };
  }, [aberto, det, conta, p.ship]);

  const susp = p.divergencia !== 'nenhuma';

  return (
    <div className={`prow ${aberto ? 'prow--open' : ''} ${p.divergencia === 'confirmada' ? 'prow--div' : susp ? 'prow--susp' : ''}`}>
      <button className="prow-head" onClick={onToggle}>
        <DivBadge p={p} />
        <span className="prow-ped mono">#{p.pedido}</span>
        <span className="prow-cli" title={p.cliente}>{p.cliente}</span>
        {p.apelido && <span className="prow-apel">{p.apelido}</span>}
        <span className="prow-dest">{p.cidade ? `${p.cidade}${p.uf ? '/' + p.uf : ''}` : '—'}</span>
        <span className="prow-st">{p.status || '—'}{p.substatus ? <em> · {p.substatus}</em> : null}</span>
        <span className="prow-val mono">{brl(p.valor)}</span>
        <FontAwesomeIcon icon={faChevronDown} className={`prow-chev ${aberto ? 'up' : ''}`} />
      </button>

      {aberto && (
        <div className="prow-body">
          {loading ? <div className="exp-msg mini">Consultando o Mercado Livre…</div>
            : erro ? <div className="exp-msg exp-msg--err mini">{erro}</div>
            : det ? (
              <>
                <div className={`dvg dvg--${det.divergencia.tipo}`}>
                  <FontAwesomeIcon icon={det.divergencia.atrasado ? faTriangleExclamation : det.divergencia.tipo === 'ok' ? faCircleCheck : faCodeBranch} />
                  <span>{det.divergencia.mensagem}</span>
                </div>
                <div className="dcols">
                  <div className="dcol">
                    <div className="dcol-h"><FontAwesomeIcon icon={faBoxesStacked} /> Itens ({det.itens.length})</div>
                    {det.itens.length === 0 ? <div className="dmuted">Sem itens no envio.</div> : det.itens.map((it, i) => (
                      <div className="item" key={i}>
                        <span className="item-q">{it.quantidade}×</span>
                        <div>
                          <div className="item-desc" title={it.descricao}>{it.descricao}</div>
                          <div className="item-id mono">{it.id}{it.dimensions ? ` · ${it.dimensions}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="dcol">
                    <div className="dcol-h"><FontAwesomeIcon icon={faCodeBranch} /> Interno × Plataforma</div>
                    <Kv k="ZipHub" v={det.interno?.status || '—'} />
                    <Kv k="ML" v={`${det.plataforma.status || '—'} · ${det.plataforma.substatus || '—'}`} />
                    <Kv k="Logística" v={`${det.plataforma.logistic_type || '—'} (${det.plataforma.mode || '—'})`} />
                    <Kv k="Prazo despacho" v={fmtData(det.plataforma.prazo_despacho)} strong={det.divergencia.atrasado} />
                    <Kv k="Buffering" v={fmtData(det.plataforma.buffering_date)} />
                    <Kv k="Est. entrega" v={fmtData(det.plataforma.estimativa_entrega)} />
                  </div>
                  <div className="dcol">
                    <div className="dcol-h"><FontAwesomeIcon icon={faLocationDot} /> Envio & destino</div>
                    <Kv k="Ship" v={det.ship} mono />
                    <Kv k="Rastreio" v={det.plataforma.tracking_number || '—'} mono />
                    <Kv k="Método" v={det.plataforma.tracking_method || '—'} />
                    <Kv k="Handling" v={fmtDataHora(det.plataforma.date_handling)} />
                    <Kv k="Enviado em" v={fmtDataHora(det.plataforma.date_shipped)} />
                    <Kv k="Destino" v={det.plataforma.receiver.cidade ? `${det.plataforma.receiver.cidade}/${det.plataforma.receiver.uf || ''} · ${det.plataforma.receiver.cep || ''}` : '—'} />
                  </div>
                </div>
                <button className="raw-toggle" onClick={() => setVerRaw((x) => !x)}>{verRaw ? 'ocultar' : 'ver'} JSON completo do envio</button>
                {verRaw && <pre className="raw">{JSON.stringify(det.raw, null, 2)}</pre>}
              </>
            ) : null}
        </div>
      )}
    </div>
  );
}

function Kv({ k, v, mono, strong }) {
  return <div className="kv"><span className="kv-k">{k}</span><span className={`kv-v ${mono ? 'mono' : ''} ${strong ? 'kv-strong' : ''}`}>{v}</span></div>;
}

function DivBadge({ p }) {
  if (p.divergencia === 'confirmada')
    return <span className="bdg bdg-div"><FontAwesomeIcon icon={faCodeBranch} /> Divergente</span>;
  if (p.divergencia === 'suspeita')
    return <span className="bdg bdg-susp" title="Suspeito — não deu pra confirmar no ML agora"><FontAwesomeIcon icon={faQuestion} /> Verificar</span>;
  if (p.divergencia === 'resolvida')
    return <span className="bdg bdg-done" title="O ML já despachou"><FontAwesomeIcon icon={faCircleCheck} /> OK no ML</span>;
  if (p.situacaoDespacho === 'atrasado') return <span className="bdg bdg-late"><FontAwesomeIcon icon={faTriangleExclamation} /> Atrasado</span>;
  if (p.situacaoDespacho === 'despachado') return <span className="bdg bdg-done"><FontAwesomeIcon icon={faCircleCheck} /> Enviado</span>;
  return <span className="bdg bdg-ontime"><FontAwesomeIcon icon={faClock} /> No prazo</span>;
}

const CSS = `
.exp-wrap{--ink:#0f172a;--muted:#64748b;--line:#e6eaf0;--surface:#fff;--bg:#f5f7fa;
  --teal:#0d9488;--late:#e11d48;--ontime:#f59e0b;--done:#94a3b8;--div:#7c3aed;
  max-width:1240px;margin:0 auto;padding:26px 22px 60px;color:var(--ink);
  font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;}
.exp-wrap *{box-sizing:border-box;}
.exp-eyebrow{font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--teal);}
.exp-head{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;margin-bottom:22px;}
.exp-title{font-size:26px;font-weight:800;letter-spacing:-.02em;margin:2px 0 0;display:flex;align-items:center;gap:10px;}
.exp-title svg{color:var(--teal);}
.exp-controls{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.exp-store{display:flex;align-items:center;gap:8px;height:38px;padding:0 12px;border:1px solid var(--line);border-radius:10px;background:var(--surface);}
.exp-store svg{color:var(--muted);}
.exp-store select{border:0;background:none;font:inherit;font-weight:600;font-size:13px;color:var(--ink);outline:none;cursor:pointer;padding-right:4px;}
.exp-nav{display:flex;align-items:center;gap:8px;}
.exp-navbtn{width:38px;height:38px;border-radius:10px;border:1px solid var(--line);background:var(--surface);color:var(--ink);cursor:pointer;transition:.15s;}
.exp-navbtn:hover{border-color:var(--teal);color:var(--teal);}
.exp-week{display:flex;align-items:center;gap:8px;font-weight:600;font-size:14px;padding:0 12px;min-width:186px;justify-content:center;}
.exp-week svg{color:var(--muted);}
.exp-today{height:38px;padding:0 16px;border-radius:10px;border:1px solid var(--teal);background:var(--teal);color:#fff;font-weight:600;cursor:pointer;font-size:13px;}
.exp-today:hover{filter:brightness(1.06);}

.exp-stats{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-bottom:22px;}
.stat{background:var(--surface);border:1px solid var(--line);border-radius:14px;padding:16px 18px;}
.stat-n{font-size:28px;font-weight:800;font-variant-numeric:tabular-nums;line-height:1;display:flex;align-items:center;gap:8px;}
.stat-n svg{font-size:15px;}
.stat-l{margin-top:6px;font-size:12px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.06em;}
.stat--div .stat-n{color:var(--div);}
.stat--late .stat-n{color:var(--late);}
.stat--ontime .stat-n{color:var(--ontime);}
.stat--done .stat-n{color:#64748b;}
@media(max-width:1080px){.exp-stats{grid-template-columns:repeat(3,1fr);}}

.exp-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:12px;align-items:stretch;}
@media(max-width:1080px){.exp-grid{grid-template-columns:repeat(4,1fr);}}
@media(max-width:640px){.exp-grid{grid-template-columns:repeat(2,1fr);}}

.dia{position:relative;text-align:left;display:flex;flex-direction:column;gap:9px;min-height:238px;background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:14px;cursor:pointer;transition:transform .12s,box-shadow .12s,border-color .12s;font:inherit;color:inherit;}
.dia:not(:disabled):hover{transform:translateY(-3px);box-shadow:0 12px 26px rgba(15,23,42,.10);border-color:#cfd8e3;}
.dia--hoje{border-color:var(--teal);box-shadow:0 0 0 1px var(--teal) inset;}
.dia--alerta{background:linear-gradient(180deg,#fff 60%,#fff5f7);}
.dia--div{background:linear-gradient(180deg,#fff 55%,#f5f3ff);border-color:#ddd6fe;}
.dia--vazio{cursor:default;background:#fafbfc;}
.dia-top{display:flex;align-items:center;gap:8px;}
.dia-nome{font-weight:800;font-size:13px;letter-spacing:.08em;color:var(--muted);}
.dia-data{font-size:12px;color:var(--muted);font-variant-numeric:tabular-nums;}
.dia-hoje-tag{margin-left:auto;background:var(--teal);color:#fff;font-size:10px;font-weight:700;padding:2px 7px;border-radius:20px;text-transform:uppercase;letter-spacing:.06em;}
.dia-num{display:flex;align-items:baseline;gap:6px;}
.dia-num-val{font-size:36px;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:-.03em;line-height:1;}
.dia-num-lbl{font-size:12px;color:var(--muted);}
.dia-divtag{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:700;color:#fff;background:var(--div);padding:3px 8px;border-radius:20px;width:fit-content;}
.dia-divtag svg{font-size:10px;}
.dia-empty{color:#aab4c0;font-size:13px;margin-top:auto;}
.dia-load{flex:1;display:flex;align-items:flex-end;min-height:48px;}
.dia-load-bar{width:26px;display:flex;flex-direction:column-reverse;border-radius:7px;overflow:hidden;background:#eef1f5;min-height:14px;}
.seg{display:block;width:100%;}
.seg-late{background:var(--late);}.seg-ontime{background:var(--ontime);}.seg-done{background:var(--done);}
.dia-badges{display:flex;flex-direction:column;gap:4px;}
.bdg{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:600;padding:3px 8px;border-radius:20px;width:fit-content;}
.bdg svg{font-size:10px;}
.bdg-late{background:#ffe4ea;color:#be123c;}
.bdg-ontime{background:#fef3c7;color:#b45309;}
.bdg-done{background:#eef2f6;color:#64748b;}
.bdg-div{background:#ede9fe;color:#6d28d9;}
.bdg-susp{background:#fff7ed;color:#c2410c;}
.dia-status{display:flex;flex-wrap:wrap;gap:4px;margin-top:2px;}
.stchip{font-size:10.5px;color:#5b6674;background:#f1f4f8;border-radius:6px;padding:2px 6px;}
.stchip b{color:var(--ink);}

.exp-legend{display:flex;flex-wrap:wrap;gap:16px;margin-top:22px;font-size:12px;color:var(--muted);}
.exp-legend .dot{display:inline-block;width:10px;height:10px;border-radius:3px;margin-right:6px;vertical-align:middle;}
.dot-div{background:var(--div);}.dot-late{background:var(--late);}.dot-ontime{background:var(--ontime);}.dot-done{background:var(--done);}

.exp-msg{background:#fff;border:1px dashed var(--line);border-radius:12px;padding:26px;text-align:center;color:var(--muted);}
.exp-msg.mini{padding:14px;font-size:13px;}
.exp-msg--err{border-style:solid;border-color:#fecdd3;background:#fff1f3;color:#be123c;}

.modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.42);backdrop-filter:blur(2px);display:flex;align-items:flex-start;justify-content:center;padding:5vh 16px;z-index:1050;}
.modal-card{background:#fff;border-radius:18px;width:min(1020px,100%);max-height:88vh;display:flex;flex-direction:column;box-shadow:0 24px 60px rgba(15,23,42,.28);overflow:hidden;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;color:#0f172a;}
.modal-head{display:flex;justify-content:space-between;align-items:flex-start;padding:20px 22px;border-bottom:1px solid #eef1f5;}
.modal-title{font-size:20px;font-weight:800;margin:2px 0 0;letter-spacing:-.01em;}
.modal-div{display:inline-flex;align-items:center;gap:6px;margin-top:6px;font-size:12px;font-weight:600;color:#6d28d9;background:#ede9fe;padding:3px 10px;border-radius:20px;}
.modal-x{width:36px;height:36px;border-radius:10px;border:1px solid #e6eaf0;background:#fff;cursor:pointer;color:#475569;}
.modal-x:hover{background:#f1f4f8;}
.modal-body{padding:14px 16px 18px;overflow:auto;}
.ped-hint{font-size:12px;color:#94a3b8;margin:0 4px 10px;}
.ped-list{display:flex;flex-direction:column;gap:8px;}

.fbar{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:4px;}
.fbtn{display:inline-flex;align-items:center;gap:7px;padding:7px 13px;border-radius:22px;border:1px solid #e6eaf0;
  background:#fff;color:#475569;font:inherit;font-size:13px;font-weight:600;cursor:pointer;transition:.12s;}
.fbtn svg{font-size:11px;}
.fbtn:hover:not(:disabled){border-color:#cbd5e1;}
.fbtn:disabled{opacity:.45;cursor:default;}
.fbtn-n{font-variant-numeric:tabular-nums;background:#eef1f5;color:#64748b;border-radius:20px;padding:1px 8px;font-size:12px;}
.fbtn--on{color:#fff;}
.fbtn--on .fbtn-n{background:rgba(255,255,255,.28);color:#fff;}
.fbtn--ink.fbtn--on{background:#0f172a;border-color:#0f172a;}
.fbtn--div.fbtn--on{background:var(--div);border-color:var(--div);}
.fbtn--late.fbtn--on{background:var(--late);border-color:var(--late);}
.fbtn--ontime.fbtn--on{background:var(--ontime);border-color:var(--ontime);}
.fbtn--susp.fbtn--on{background:#ea580c;border-color:#ea580c;}

.prow{border:1px solid #eceff3;border-radius:12px;overflow:hidden;background:#fff;transition:border-color .12s;}
.prow--open{border-color:#d7dce4;box-shadow:0 6px 20px rgba(15,23,42,.06);}
.prow--susp{border-color:#fed7aa;}
.prow--div{border-color:#ddd6fe;}
.prow-head{width:100%;display:grid;grid-template-columns:112px 74px 1.3fr 64px 1fr 1fr 88px 20px;align-items:center;gap:10px;padding:11px 14px;background:none;border:0;cursor:pointer;text-align:left;font:inherit;color:inherit;}
.prow-head:hover{background:#fafbfc;}
.prow-ped{color:#334155;font-weight:600;}
.prow-cli{font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.prow-apel{font-size:11px;font-weight:700;color:#0d9488;background:#ecfdf5;border-radius:6px;padding:2px 7px;text-align:center;}
.prow-dest,.prow-st{font-size:12.5px;color:#64748b;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.prow-st em{color:#94a3b8;font-style:normal;}
.prow-val{text-align:right;color:#334155;}
.prow-chev{color:#94a3b8;transition:transform .15s;}
.prow-chev.up{transform:rotate(180deg);}
@media(max-width:820px){.prow-head{grid-template-columns:112px 1fr 24px;}.prow-dest,.prow-st,.prow-val,.prow-ped,.prow-apel{display:none;}}

.prow-body{padding:4px 14px 16px;border-top:1px solid #f2f5f8;background:#fcfdfe;}
.dvg{display:flex;align-items:center;gap:9px;padding:11px 13px;border-radius:10px;font-size:13px;font-weight:600;margin:12px 0;}
.dvg svg{font-size:14px;flex-shrink:0;}
.dvg--ok{background:#ecfdf5;color:#047857;}
.dvg--atendido_pendente{background:#fef3c7;color:#b45309;}
.dvg--atendido_atrasado,.dvg--atrasado{background:#ffe4ea;color:#be123c;}
.dvg--enviado_nao_finalizado{background:#ede9fe;color:#6d28d9;}
.dcols{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;}
@media(max-width:820px){.dcols{grid-template-columns:1fr;}}
.dcol{background:#fff;border:1px solid #eef1f5;border-radius:10px;padding:12px;}
.dcol-h{display:flex;align-items:center;gap:7px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#64748b;margin-bottom:10px;}
.dcol-h svg{color:#94a3b8;}
.dmuted{color:#94a3b8;font-size:13px;}
.item{display:flex;gap:9px;padding:7px 0;border-bottom:1px solid #f4f6f9;}
.item:last-child{border-bottom:0;}
.item-q{font-weight:800;color:#0d9488;font-variant-numeric:tabular-nums;font-size:13px;padding-top:1px;}
.item-desc{font-size:12.5px;font-weight:600;line-height:1.3;}
.item-id{font-size:11px;color:#94a3b8;margin-top:2px;}
.kv{display:flex;justify-content:space-between;gap:10px;padding:5px 0;border-bottom:1px solid #f4f6f9;font-size:12.5px;}
.kv:last-child{border-bottom:0;}
.kv-k{color:#94a3b8;}
.kv-v{font-weight:600;text-align:right;}
.kv-strong{color:#be123c;}
.mono{font-variant-numeric:tabular-nums;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;}
.raw-toggle{margin-top:12px;background:none;border:0;color:#0d9488;font-size:12px;font-weight:600;cursor:pointer;padding:0;}
.raw{margin-top:8px;background:#0f172a;color:#cbd5e1;border-radius:10px;padding:12px;font-size:11px;max-height:280px;overflow:auto;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;}
`;